import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Loader2, Copy, CheckCircle2, Download, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { CRM_API, AUTH_API, errMsg } from "./api";
import SearchSelect from "./components/SearchSelect";
import { vehicleLabel } from "@/lib/utils";
import { buildQuoteText, fmt2, copyText } from "./quoteText";

const money = (v) => (v == null ? null : `$${Number(v).toLocaleString("en-CA")}`);
const dur = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
};

export default function QuickQuote() {
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [clsOverride, setClsOverride] = useState(""); // staff override for custom/trim model names
  const [selected, setSelected] = useState({}); // key → {label, amount, note?}
  const [copied, setCopied] = useState(false);
  const [emailForm, setEmailForm] = useState(false);
  const [qEmail, setQEmail] = useState("");
  const [qName, setQName] = useState("");
  const [sending, setSending] = useState(false);

  const toggleSel = (key, line) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = line;
      return next;
    });

  // Only one PPF package can be quoted at a time
  const togglePkg = (key, line) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) { delete next[key]; return next; }
      for (const k of Object.keys(next)) if (k.startsWith("ppf-pkg:")) delete next[k];
      next[key] = line;
      return next;
    });

  const selCount = Object.keys(selected).length;
  const selSubtotal = Object.values(selected).reduce((s, l) => s + l.amount, 0);
  const selGst = Math.round(selSubtotal * 5) / 100;

  const quotePayload = () => ({
    email: qEmail, customer_name: qName,
    year: Number(year), make, model,
    lines: Object.values(selected).map((l) => ({ label: l.label, amount: l.amount, note: l.note || null })),
    full_vehicle_preliminary: Object.keys(selected).includes("ppf-pkg:full-vehicle"),
  });

  const sendQuoteEmail = async () => {
    setSending(true);
    try {
      await CRM_API.post("/quick-quote/email", quotePayload());
      toast.success(`Quote sent to ${qEmail}`);
      setEmailForm(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const downloadQuotePdf = async () => {
    try {
      const { data } = await CRM_API.post("/quick-quote/pdf", { ...quotePayload(), email: qEmail || "quote@wtd.ca" }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `WTD-Quote-${year}-${make}-${model}.pdf`.replace(/\s+/g, "-");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyQuote = async () => {
    const fv = Object.keys(selected).includes("ppf-pkg:full-vehicle");
    const text = buildQuoteText({
      vehicleLabel: `${year} ${make} ${model}`,
      lines: Object.values(selected),
      subtotal: selSubtotal,
      taxRate: 5,
      tax: selGst,
      total: Math.round((selSubtotal + selGst) * 100) / 100,
      fullVehiclePreliminary: fv,
    });
    const ok = await copyText(text);
    if (ok) {
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Copy failed — clipboard blocked by the browser");
    }
  };

  useEffect(() => {
    AUTH_API.get("/vehicles/years").then(({ data }) => setYears(data)).catch(() => {});
    AUTH_API.get("/vehicles/makes").then(({ data }) => setMakes(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setModels([]);
    setModel("");
    setResult(null);
    setClsOverride("");
    if (!year || !make) return;
    setModelsLoading(true);
    AUTH_API.get("/vehicles/models", { params: { year, make } })
      .then(({ data }) => setModels(data))
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [year, make]);

  useEffect(() => {
    setResult(null);
    setSelected({});
    setError("");
    if (!year || !make || !model) return;
    setLoading(true);
    const params = { year, make, model };
    if (clsOverride) params.vehicle_class = clsOverride;
    CRM_API.get("/quick-quote", { params })
      .then(({ data }) => setResult(data))
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [year, make, model, clsOverride]);

  const groups = useMemo(() => {
    if (!result) return [];
    const map = new Map();
    for (const s of result.services) {
      const g = s.group || "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(s);
    }
    return [...map.entries()];
  }, [result]);

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="quick-quote-page">
      <div className="flex items-center gap-3 mb-1">
        <Calculator className="w-6 h-6 text-[#8db2ff]" />
        <h1 className="text-2xl font-bold text-white">Quick Quote</h1>
      </div>
      <p className="text-white/45 text-sm mb-6">
        Pick the vehicle — see every service priced for its size class, same as the website calculator.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <SearchSelect value={year} onChange={setYear} options={years} placeholder="Year" allowCustom testid="qq-year" />
        <SearchSelect value={make} onChange={(v) => { setMake(v); }} options={makes} placeholder="Make" disabled={!year} allowCustom testid="qq-make" />
        <SearchSelect value={model} onChange={setModel} options={models} placeholder={modelsLoading ? "Loading…" : "Model (e.g. Sierra Elevation)"} disabled={!make || modelsLoading} loading={modelsLoading} allowCustom testid="qq-model" />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-white/50 text-sm py-8" data-testid="qq-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Pricing {vehicleLabel(year, make, model)}…
        </div>
      )}
      {error && <div className="text-red-400 text-sm py-4" data-testid="qq-error">{error}</div>}

      {result && (
        <div data-testid="qq-results">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="text-white/70 text-sm">{vehicleLabel(year, make, model)}</span>
            <span className="text-white/40 text-xs">Class:</span>
            {["D1", "D2", "D3", "D4", "D5"].map((c) => (
              <button
                key={c}
                onClick={() => setClsOverride(clsOverride === c ? "" : c)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  (clsOverride || result.vehicle_class) === c
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                    : "bg-white/5 text-white/40 border-white/10 hover:text-white/70"
                }`}
                data-testid={`qq-class-${c}`}
                title={c === "D1" ? "Compact" : c === "D2" ? "Mid-size sedan" : c === "D3" ? "Full-size / small SUV" : c === "D4" ? "SUV / pickup" : "XL truck / van"}
              >
                {c}
              </button>
            ))}
            {result.promo?.active && (
              <span className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-[#1F5EFF]/15 text-[#8db2ff] border border-[#1F5EFF]/30" data-testid="qq-promo">
                {result.promo.badge || "Promo"} — UP TO {result.promo.percent}% OFF eligible
              </span>
            )}
            <span className="flex-1" />
            {selCount > 0 && (
              <>
                <span className="text-white/60 text-xs" data-testid="qq-selected-summary">
                  {selCount} selected · {fmt2(selSubtotal)} + GST
                </span>
                <button onClick={copyQuote} data-testid="qq-copy-quote"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-xs font-semibold transition-colors">
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied!" : "Copy Quote"}
                </button>
                <button onClick={downloadQuotePdf} data-testid="qq-pdf"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-white/80 text-xs font-semibold transition-colors">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button onClick={() => setEmailForm((v) => !v)} data-testid="qq-email-toggle"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-white/80 text-xs font-semibold transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Email
                </button>
              </>
            )}
          </div>
          {selCount > 0 && emailForm && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-3" data-testid="qq-email-form">
              <input value={qName} onChange={(e) => setQName(e.target.value)} placeholder="Customer name (optional)"
                className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#1F5EFF]/60 w-52"
                data-testid="qq-email-name" />
              <input value={qEmail} onChange={(e) => setQEmail(e.target.value)} placeholder="customer@email.com" type="email"
                className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#1F5EFF]/60 flex-1 min-w-48"
                data-testid="qq-email-address" />
              <button onClick={sendQuoteEmail} disabled={sending || !qEmail.includes("@")}
                data-testid="qq-email-send"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-xs font-semibold transition-colors disabled:opacity-40">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send quote
              </button>
            </div>
          )}
          <p className="text-white/30 text-[11px] mb-4">Tap services, PPF packages or panels to include them in the quote, then copy it for the customer.</p>

          <div className="space-y-5">
            {groups.map(([group, items]) => (
              <div key={group} className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-white/8 font-mono-alt text-[10px] uppercase tracking-widest text-white/40">
                  {group}
                </div>
                <div className="divide-y divide-white/5">
                  {items.map((s) => {
                    const selKey = `svc:${s.key}`;
                    const selectable = !s.options && s.price != null;
                    const isSel = Boolean(selected[selKey]);
                    const line = { label: s.name, amount: s.promo_price ?? s.price, note: s.promo_price != null ? "Grand Opening price" : undefined };
                    return (
                    <div key={s.key} data-testid={`qq-service-${s.key}`}
                      onClick={selectable ? () => toggleSel(selKey, line) : undefined}
                      className={`px-4 py-3 flex items-center gap-3 ${selectable ? "cursor-pointer" : ""} ${isSel ? "bg-[#1F5EFF]/10" : ""}`}>
                      {selectable && (
                        <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSel ? "bg-[#1F5EFF] border-[#1F5EFF]" : "border-white/20"}`} data-testid={`qq-sel-${s.key}`}>
                          {isSel && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{s.name}</div>
                        <div className="text-white/35 text-xs">{dur(s.duration_min)}</div>
                        {s.options && (
                          <div className="mt-1 space-y-0.5">
                            {s.options.map((o) => (
                              <div key={o.key} className="text-xs text-white/55">
                                {o.label}: <span className="text-white font-medium">{o.price != null ? money(o.price) : "custom"}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {!s.options && (
                        <div className="text-right shrink-0">
                          {s.price == null ? (
                            <span className="text-white/45 text-sm">Custom quote</span>
                          ) : s.promo_price != null ? (
                            <>
                              <div className="text-white/35 text-xs line-through">{money(s.price)}</div>
                              <div className="text-emerald-300 font-bold">{money(s.promo_price)}</div>
                            </>
                          ) : (
                            <div className="text-white font-bold">
                              {s.price_type === "starting" && <span className="text-white/40 text-xs font-normal">from </span>}
                              {money(s.price)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* PPF — packages, upgrades and individual panels for this vehicle class.
                Same centralized pricing as the website calculator; PPF never joins promos. */}
            {result.ppf && (
              <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden" data-testid="qq-ppf">
                <div className="px-4 py-2.5 border-b border-white/8 font-mono-alt text-[10px] uppercase tracking-widest text-white/40">
                  Paint Protection Film ({result.ppf.film_body}) — no promo
                </div>
                <div className="divide-y divide-white/5">
                  {result.ppf.packages.map((p) => {
                    const selKey = `ppf-pkg:${p.key}`;
                    const isSel = Boolean(selected[selKey]);
                    const amount = p.quote_only ? p.preliminary_price : p.price;
                    const line = { label: `${p.name} PPF`, amount, note: p.quote_only ? "preliminary — final price after free inspection" : undefined };
                    return (
                    <div key={p.key} data-testid={`qq-ppf-pkg-${p.key}`}
                      onClick={() => amount != null && togglePkg(selKey, line)}
                      className={`px-4 py-3 flex items-center gap-3 ${amount != null ? "cursor-pointer" : ""} ${isSel ? "bg-[#1F5EFF]/10" : ""}`}>
                      {amount != null && (
                        <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSel ? "bg-[#1F5EFF] border-[#1F5EFF]" : "border-white/20"}`}>
                          {isSel && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm">{p.name} PPF</div>
                        {p.quote_only && (
                          <div className="text-amber-300/70 text-xs">
                            Preliminary estimate — final price confirmed after a free 30-min vehicle inspection
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0 text-white font-bold">
                        {p.quote_only
                          ? <>{money(p.preliminary_price)} <span className="text-white/40 text-xs font-normal">+ GST</span></>
                          : money(p.price)}
                      </div>
                    </div>
                    );
                  })}
                  {(() => {
                    const w = result.ppf.windshield_price;
                    const selKey = "ppf-ws";
                    const isSel = Boolean(selected[selKey]);
                    return (
                  <div data-testid="qq-ppf-windshield"
                    onClick={w != null ? () => toggleSel(selKey, { label: "Windshield Protection Film", amount: w }) : undefined}
                    className={`px-4 py-3 flex items-center gap-3 ${w != null ? "cursor-pointer" : ""} ${isSel ? "bg-[#1F5EFF]/10" : ""}`}>
                    {w != null && (
                      <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSel ? "bg-[#1F5EFF] border-[#1F5EFF]" : "border-white/20"}`}>
                        {isSel && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm">Windshield Protection Film</div>
                      <div className="text-white/35 text-xs">not included in Full Vehicle</div>
                    </div>
                    <div className="text-right shrink-0 text-white font-bold">
                      {w != null ? money(w) : "Custom quote"}
                    </div>
                  </div>
                    );
                  })()}
                  {result.ppf.upgrades.map((u) => {
                    const selKey = `ppf-upg:${u.key}`;
                    const isSel = Boolean(selected[selKey]);
                    return (
                    <div key={u.key} data-testid={`qq-ppf-upgrade-${u.key}`}
                      onClick={() => toggleSel(selKey, { label: u.label, amount: u.amount })}
                      className={`px-4 py-3 flex items-center gap-3 cursor-pointer bg-amber-400/[0.04] ${isSel ? "!bg-amber-400/15" : ""}`}>
                      <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSel ? "bg-amber-400 border-amber-400" : "border-amber-300/30"}`}>
                        {isSel && <CheckCircle2 className="w-3 h-3 text-black" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm">↑ {u.label}</div>
                        <div className="text-white/35 text-xs">{u.note}</div>
                      </div>
                      <div className="text-right shrink-0 text-amber-200 font-bold">+{money(u.amount)}</div>
                    </div>
                    );
                  })}
                  {result.ppf.parts.map((p) => {
                    // Default variant for quoting: pair / all 4 / flat
                    const amount = p.kind === "flat" ? p.price : p.kind === "sided" ? p.pair : p.all4;
                    const suffix = p.kind === "sided" ? " (pair)" : p.kind === "count4" ? " (all 4)" : "";
                    const selKey = `ppf-part:${p.key}`;
                    const isSel = Boolean(selected[selKey]);
                    return (
                    <div key={p.key} data-testid={`qq-ppf-part-${p.key}`}
                      onClick={() => toggleSel(selKey, { label: `${p.label}${suffix}`, amount })}
                      className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer ${isSel ? "bg-[#1F5EFF]/10" : ""}`}>
                      <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${isSel ? "bg-[#1F5EFF] border-[#1F5EFF]" : "border-white/20"}`}>
                        {isSel && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-white/80 text-sm">{p.label}</div>
                        {p.class_scaled && <div className="text-white/30 text-[10px]">scales with vehicle size</div>}
                      </div>
                      <div className="text-right shrink-0 text-white/80 text-sm tabular-nums">
                        {p.kind === "flat" && money(p.price)}
                        {p.kind === "sided" && `${money(p.single)} / ${money(p.pair)} pair`}
                        {p.kind === "count4" && `${money(p.each)} ea${p.pair != null ? ` / ${money(p.pair)} pair` : ""} / ${money(p.all4)} all 4`}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <p className="text-white/30 text-xs mt-4">
            Estimates match the website calculator. Final price is confirmed at drop-off after the vehicle inspection.
          </p>
        </div>
      )}
    </div>
  );
}
